import {
  getOrderedQuestions,
  injectedAnswerKey,
} from '@/components/questionnaires/json-driven/DynamicQuestionnaireFlow';
import type { QuestionnaireDemographicContext } from '@/components/questionnaires/json-driven/clientContext';
import { getQuestionKey, buildQuestionIdToStorageKeyMap } from '@/components/questionnaires/json-driven/questionKey';
import {
  isAnsweredValue,
  normalizeQuestionAnswerForSubmit,
  OTHER_TEXT_SUFFIX,
  type JsonAnswerValue,
} from '@/components/questionnaires/json-driven/QuestionRenderer';
import { getVisibleOrderedQuestions } from '@/components/questionnaires/json-driven/visibleQuestions';
import type { JsonQuestion, JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import type { TriggeredBlock } from '@/lib/questionnaire-triggers';
import type {
  QuestionnaireSavedResponse,
  SubmitQuestionnaireBlock,
  SubmitQuestionnaireItem,
} from '@/lib/api/questionnaires';

type AnswerMap = Record<string, JsonAnswerValue>;

export function isQuestionAnswerNonEmpty(question: JsonQuestion, value: JsonAnswerValue | undefined): boolean {
  if (value === undefined) return false;
  if (question.type === 'number-unit') {
    return normalizeQuestionAnswerForSubmit(question, value) !== undefined;
  }
  return isAnsweredValue(value);
}

export function getTriggeredSectionAnswers(
  section: JsonQuestionnaireSection,
  answers: AnswerMap,
  storageSectionId: string
): AnswerMap {
  const sectionAnswers: AnswerMap = {};
  for (const question of getOrderedQuestions(section)) {
    const questionId = getQuestionKey(question);
    const answer = answers[injectedAnswerKey(storageSectionId, questionId)];
    if (answer !== undefined) {
      sectionAnswers[questionId] = answer;
    }
    const otherVal = answers[injectedAnswerKey(storageSectionId, questionId + OTHER_TEXT_SUFFIX)];
    if (otherVal !== undefined) {
      sectionAnswers[questionId + OTHER_TEXT_SUFFIX] = otherVal;
    }
  }
  return sectionAnswers;
}

function buildSavedResponseStorageKeyMap(
  section: JsonQuestionnaireSection,
  triggeredBlocks: TriggeredBlock[]
): Map<string, string> {
  const map = buildQuestionIdToStorageKeyMap(section.questions);

  for (const block of triggeredBlocks) {
    const storageId = block.meta.questionnaireId;
    for (const question of block.section.questions ?? []) {
      const storageKey = injectedAnswerKey(storageId, getQuestionKey(question));
      map.set(question._id, storageKey);
      const externalId = question.externalId?.trim();
      if (externalId) map.set(externalId, storageKey);
      map.set(getQuestionKey(question), storageKey);
    }
  }

  return map;
}

/** Stable fingerprint so updated draft answers for the same questionId can re-apply. */
export function savedResponseFingerprint(item: Pick<QuestionnaireSavedResponse, 'answer' | 'otherText'>): string {
  return JSON.stringify({
    answer: item.answer ?? null,
    otherText: item.otherText ?? null,
  });
}

/**
 * Maps flat savedResponses from the API into answer-map storage keys.
 * Primary questions use plain keys; triggered questionnaires use injected keys.
 * Re-applies when the saved answer for a questionId changed (e.g. multi-select updates).
 */
export function applyPendingSavedResponses(
  section: JsonQuestionnaireSection,
  triggeredBlocks: TriggeredBlock[],
  savedResponses: QuestionnaireSavedResponse[] | undefined,
  appliedQuestionFingerprints: Map<string, string>
): AnswerMap {
  if (!savedResponses?.length) return {};

  const storageKeyByQuestionId = buildSavedResponseStorageKeyMap(section, triggeredBlocks);
  const patch: AnswerMap = {};

  for (const item of savedResponses) {
    const fingerprint = savedResponseFingerprint(item);
    if (appliedQuestionFingerprints.get(item.questionId) === fingerprint) continue;

    const storageKey = storageKeyByQuestionId.get(item.questionId);
    if (!storageKey) continue;

    if (item.answer != null) {
      patch[storageKey] = item.answer as JsonAnswerValue;
    }
    if (item.otherText != null) {
      patch[storageKey + OTHER_TEXT_SUFFIX] = item.otherText;
    }
    appliedQuestionFingerprints.set(item.questionId, fingerprint);
  }

  return patch;
}

/** Flatten submit blocks into the savedResponses shape used by GET share. */
export function savedResponsesFromBlocks(
  blocks: SubmitQuestionnaireBlock[]
): QuestionnaireSavedResponse[] {
  const byQuestionId = new Map<string, QuestionnaireSavedResponse>();
  for (const block of blocks) {
    for (const response of block.responses) {
      byQuestionId.set(response.questionId, {
        questionId: response.questionId,
        answer: (response.answer ?? null) as QuestionnaireSavedResponse['answer'],
        ...(typeof response.otherText === 'string' ? { otherText: response.otherText } : {}),
      });
    }
  }
  return Array.from(byQuestionId.values());
}

function normalizeOtherTextForSubmit(value: JsonAnswerValue | undefined): string | undefined {
  const text = Array.isArray(value) ? value.join(', ') : value;
  return typeof text === 'string' && text.trim().length > 0 ? text.trim() : undefined;
}

function toResponseItem(
  question: JsonQuestion,
  answers: AnswerMap,
  onlyNonEmpty: boolean
): SubmitQuestionnaireItem | null {
  const questionId = getQuestionKey(question);
  const rawAnswer = answers[questionId];
  const otherText = answers[questionId + OTHER_TEXT_SUFFIX];
  const answer = normalizeQuestionAnswerForSubmit(question, rawAnswer);
  const normalizedOtherText = normalizeOtherTextForSubmit(otherText);

  if (onlyNonEmpty && !isQuestionAnswerNonEmpty(question, rawAnswer)) {
    return null;
  }

  return {
    questionId,
    answer,
    ...(normalizedOtherText ? { otherText: normalizedOtherText } : {}),
  };
}

export function buildQuestionnaireBlocks(
  section: JsonQuestionnaireSection,
  answers: AnswerMap,
  triggeredBlocks: TriggeredBlock[],
  demographicContext: QuestionnaireDemographicContext | undefined,
  options?: { onlyNonEmpty?: boolean }
): SubmitQuestionnaireBlock[] {
  const onlyNonEmpty = options?.onlyNonEmpty ?? false;
  const visibleBase = getVisibleOrderedQuestions(section, answers, demographicContext);
  const baseResponses = visibleBase
    .map((question) => toResponseItem(question, answers, onlyNonEmpty))
    .filter((item): item is SubmitQuestionnaireItem => item != null);

  const blocks: SubmitQuestionnaireBlock[] = [];
  if (baseResponses.length > 0) {
    blocks.push({ questionnaireId: section.id, responses: baseResponses });
  }

  const seenTriggeredQuestionnaireIds = new Set<string>();
  for (const block of triggeredBlocks) {
    const { section: triggeredSection, meta } = block;
    const storageId = meta.questionnaireId;
    if (seenTriggeredQuestionnaireIds.has(storageId)) continue;
    seenTriggeredQuestionnaireIds.add(storageId);

    const triggeredAnswers = getTriggeredSectionAnswers(triggeredSection, answers, storageId);
    const visibleTriggered = getVisibleOrderedQuestions(
      triggeredSection,
      triggeredAnswers,
      demographicContext
    );
    const responses = visibleTriggered
      .map((question) => toResponseItem(question, triggeredAnswers, onlyNonEmpty))
      .filter((item): item is SubmitQuestionnaireItem => item != null);

    if (responses.length > 0) {
      blocks.push({ questionnaireId: storageId, responses });
    }
  }

  return blocks;
}

export function countNonEmptyResponsesInBlocks(blocks: SubmitQuestionnaireBlock[]): number {
  return blocks.reduce((total, block) => total + block.responses.length, 0);
}

export function formatDraftSavedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function isQuestionnaireInstanceLocked(
  alreadySubmitted?: boolean,
  status?: string
): boolean {
  return alreadySubmitted === true || status === 'completed';
}
